import React from 'react';
import {
  MdApi,
  MdAssessment,
  MdAutoGraph,
  MdCheckCircle,
  MdContactMail,
  MdDescription,
  MdExtension,
  MdHelpOutline,
  MdLock,
  MdOutlinePolicy,
  MdPlayCircle,
  MdRocketLaunch,
  MdStorage,
  MdSyncAlt
} from 'react-icons/md';

const FEATURE_CARDS = [
  {
    icon: MdApi,
    title: 'Reusable API registry',
    text: 'Store endpoints, auth methods, headers, and request templates once so teams can standardize how they test services.'
  },
  {
    icon: MdStorage,
    title: 'CSV-driven test data',
    text: 'Convert spreadsheet rows into repeatable scenarios, making large regression packs easier to maintain and review.'
  },
  {
    icon: MdSyncAlt,
    title: 'Multi-step workflow chaining',
    text: 'Pass captured values from one request to the next to validate realistic business flows instead of isolated calls.'
  },
  {
    icon: MdAssessment,
    title: 'Execution reporting',
    text: 'Track pass/fail counts, review captured outputs, and generate evidence for release checks and defect triage.'
  },
  {
    icon: MdExtension,
    title: 'Default and custom functions',
    text: 'Extend test data with generated values, helper utilities, and AI-assisted custom logic for advanced request shaping.'
  },
  {
    icon: MdLock,
    title: 'Authenticated workspace',
    text: 'Project data and automation assets live behind sign-in so each user works within a dedicated control panel.'
  }
];

const USE_CASES = [
  'Regression testing for internal APIs before a release window',
  'Smoke validation after deployment using a controlled CSV data set',
  'Partner integration checks where test cases need repeatable payload variations',
  'QA workflows that must capture values from one response and reuse them later'
];

const HOME_SIDE_NOTES = [
  {
    title: 'Set up faster',
    text: 'Keep reusable API definitions, auth headers, and payload templates in one workspace.'
  },
  {
    title: 'Run structured checks',
    text: 'Use CSV rows and chained steps to cover realistic request flows instead of one-off calls.'
  },
  {
    title: 'Review outcomes clearly',
    text: 'Track execution history, captured values, and failures without digging through manual notes.'
  }
];

const FAQ_ITEMS = [
  {
    question: 'What does Spider Web Automation actually do?',
    answer: 'It helps teams define APIs, upload structured test data, build reusable steps, combine them into suites, and run repeatable API checks from a browser-based control panel.'
  },
  {
    question: 'Why is login required for the app workspace?',
    answer: 'The product stores automation assets, execution histories, and account-specific configuration. Those parts of the product are intentionally private, but this public site explains the product in detail before sign-in.'
  },
  {
    question: 'Who is this best suited for?',
    answer: 'QA engineers, API testers, release managers, and technical teams who need repeatable request validation without rebuilding the same suite logic for every scenario.'
  },
  {
    question: 'How is this different from a generic API client?',
    answer: 'The focus is structured test execution at scale: reusable test steps, CSV-backed scenario generation, suite orchestration, file assets, custom functions, and execution-oriented reporting.'
  }
];

const POLICY_COPY = {
  privacy: {
    title: 'Privacy Policy',
    intro: 'Spider Web Automation collects only the information needed to provide account access, save automation assets, and return execution results to signed-in users.',
    points: [
      'Account information such as name, email address, and authentication credentials is used to identify and secure each workspace.',
      'Uploaded API definitions, CSV files, custom functions, and execution data are stored to power the product features requested by the user.',
      'Operational logs may be retained for debugging, reliability, and abuse prevention.',
      'Users should avoid uploading production secrets or personal data unless they are permitted to handle that information inside their own testing process.'
    ]
  },
  terms: {
    title: 'Terms of Use',
    intro: 'Spider Web Automation is intended for lawful API testing, workflow validation, and internal automation activities.',
    points: [
      'Users are responsible for the API targets, payloads, and data they choose to automate through the platform.',
      'The service must not be used to attack, disrupt, or gain unauthorized access to third-party systems.',
      'Generated reports and stored assets should be reviewed by the user before business or compliance decisions are made.',
      'The product owner may update features, limits, or operational controls as needed to maintain service quality and security.'
    ]
  }
};

function PublicSite({ page, onNavigate, authPanel }) {
  const renderHome = () => (
    <>
      <section className="public-hero card">
        <div>
          <p className="public-eyebrow">Product Overview</p>
          <h1>CSV-driven API automation for teams that need repeatable workflow testing.</h1>
          <p className="public-hero-copy">
            Spider Web Automation combines API definitions, uploaded test data, reusable test steps,
            execution reporting, and AI-assisted helper logic in one browser workspace. The goal is
            practical automation: less repetitive setup, clearer coverage, and faster re-runs when data changes.
          </p>
          <div className="public-hero-actions">
            <button type="button" className="btn btn-primary" onClick={() => onNavigate('login')}>
              Open Workspace
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => onNavigate('about')}>
              Learn How It Works
            </button>
          </div>
        </div>
        <div className="public-hero-panel">
          <div className="public-metric">
            <MdRocketLaunch />
            <div>
              <strong>Built for repeatability</strong>
              <span>Turn test data rows into consistent API scenarios.</span>
            </div>
          </div>
          <div className="public-metric">
            <MdPlayCircle />
            <div>
              <strong>Workflow-ready execution</strong>
              <span>Chain steps, capture values, and validate real request flows.</span>
            </div>
          </div>
          <div className="public-metric">
            <MdAutoGraph />
            <div>
              <strong>Result visibility</strong>
              <span>Review run outcomes without digging through ad hoc scripts.</span>
            </div>
          </div>
        </div>
      </section>

      <section className="public-section">
        <div className="public-section-head">
          <h2>What you can do with the platform</h2>
          <p>
            The application is designed as a working automation tool, not a placeholder login page.
            These are the main workflows available after sign-in.
          </p>
        </div>
        <div className="public-feature-grid">
          {FEATURE_CARDS.map(({ icon: Icon, title, text }) => (
            <article key={title} className="card public-feature-card">
              <div className="public-feature-icon"><Icon /></div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="public-section public-two-column">
        <article className="card">
          <h2>Example workflow</h2>
          <ol className="public-ordered-list">
            <li>Register an API collection with the endpoints your team uses regularly.</li>
            <li>Upload CSV data so each row represents a realistic test scenario.</li>
            <li>Build test steps with variables, headers, payload templates, and response capture rules.</li>
            <li>Combine the steps into a suite and run the workflow across the selected data set.</li>
            <li>Review execution results to confirm coverage, failures, and captured values.</li>
          </ol>
        </article>
        <article className="card">
          <h2>Typical use cases</h2>
          <ul className="public-list">
            {USE_CASES.map((item) => (
              <li key={item}>
                <MdCheckCircle />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>
    </>
  );

  const renderAbout = () => (
    <section className="public-section">
      <article className="card public-copy-card">
        <p className="public-eyebrow">How It Works</p>
        <h1>Why this tool exists</h1>
        <p>
          Many teams can send a single API request, but they struggle to maintain consistent multi-step
          validation across different input rows, environments, and release cycles. Spider Web Automation
          addresses that gap by combining data-driven execution with a persistent browser workspace.
        </p>
        <p>
          The product is organized around assets that technical teams already understand: API collections,
          CSV files, files and attachments, reusable test steps, suites, reports, and helper functions.
          That structure helps users move from one-off experiments to repeatable testing.
        </p>
        <p>
          The AI-related functionality is intentionally narrow. It can help generate custom helper code for
          advanced cases, but the platform still centers on user-defined workflows and manually curated test logic.
        </p>
      </article>
    </section>
  );

  const renderContact = () => (
    <section className="public-section public-two-column">
      <article className="card public-copy-card">
        <p className="public-eyebrow">Contact</p>
        <h1>Reach the Spider Web Automation team</h1>
        <p>
          For support, product feedback, or business questions, use the contact options below. This public
          page exists so visitors and reviewers can identify the owner of the site without needing to log in.
        </p>
        <div className="public-contact-list">
          <a href="mailto:raigan5805992@gmail.com">raigan5805992@gmail.com</a>
          <a href="tel:+919995805992">+91 99958 05992</a>
          <a href="https://www.linkedin.com/in/raigan-a-r-44087412a/" target="_blank" rel="noreferrer">
            LinkedIn profile
          </a>
        </div>
      </article>
      <article className="card public-copy-card">
        <h2>Support topics</h2>
        <ul className="public-list">
          <li><MdCheckCircle /><span>Account access and login help</span></li>
          <li><MdCheckCircle /><span>Questions about API setup and suite execution</span></li>
          <li><MdCheckCircle /><span>Product feedback, feature requests, and collaboration inquiries</span></li>
        </ul>
      </article>
    </section>
  );

  const renderFaq = () => (
    <section className="public-section">
      <article className="card public-copy-card">
        <p className="public-eyebrow">FAQ</p>
        <h1>Common questions</h1>
        <div className="public-faq-list">
          {FAQ_ITEMS.map((item) => (
            <details key={item.question} className="public-faq-item" open>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </article>
    </section>
  );

  const renderPolicyPage = (policyKey) => {
    const policy = POLICY_COPY[policyKey];
    return (
      <section className="public-section">
        <article className="card public-copy-card">
          <p className="public-eyebrow">{policy.title}</p>
          <h1>{policy.title}</h1>
          <p>{policy.intro}</p>
          <ul className="public-list">
            {policy.points.map((point) => (
              <li key={point}>
                <MdDescription />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>
    );
  };

  const contentByPage = {
    home: renderHome(),
    about: renderAbout(),
    contact: renderContact(),
    faq: renderFaq(),
    privacy: renderPolicyPage('privacy'),
    terms: renderPolicyPage('terms'),
    login: renderHome()
  };

  const navItems = [
    { key: 'home', label: 'Home', icon: MdRocketLaunch },
    { key: 'about', label: 'About', icon: MdOutlinePolicy },
    { key: 'faq', label: 'FAQ', icon: MdHelpOutline },
    { key: 'contact', label: 'Contact', icon: MdContactMail },
    { key: 'privacy', label: 'Privacy', icon: MdDescription },
    { key: 'terms', label: 'Terms', icon: MdOutlinePolicy }
  ];

  return (
    <div className="public-shell">
      <header className="public-header">
        <button type="button" className="public-brand" onClick={() => onNavigate('home')}>
          <img src="/spiderman-logo-white.png" alt="Spider Web Automation" className="public-logo" />
          <span>Spider Web Automation</span>
        </button>
        <nav className="public-nav" aria-label="Public navigation">
          {navItems.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              className={`public-nav-link ${page === key ? 'active' : ''}`}
              onClick={() => onNavigate(key)}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </header>

      <main className="public-main">
        <div className="public-content">
          {contentByPage[page] || contentByPage.home}
        </div>
        <aside className="public-aside">
          {authPanel}
          {page === 'home' && (
            <article className="card public-home-side-card">
              <p className="public-eyebrow">Why Teams Use It</p>
              <h2>Everything needed for repeatable API testing</h2>
              <div className="public-home-side-list">
                {HOME_SIDE_NOTES.map((item) => (
                  <div key={item.title} className="public-home-side-item">
                    <MdCheckCircle />
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          )}
        </aside>
      </main>
    </div>
  );
}

export default PublicSite;
